import { env } from './env';

/**
 * The single typed API client. Every request:
 *  - sends cookies (credentials: 'include') — session is an httpOnly cookie
 *  - carries an X-Request-Id so client logs correlate with server audit lines
 *  - normalizes server/network errors into one ApiError shape
 *  - does NOT retry by default — React Query owns the one retry layer (see
 *    lib/queryClient.ts); a caller outside React Query may opt in via `retries`
 *  - sends X-CSRF-Token from the csrf cookie on mutations (if present)
 *
 * No component calls this directly — wrap each endpoint in a TanStack Query hook
 * under features/<domain>/queries.ts.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly requestId: string;
  readonly body: unknown;
  /**
   * FIX(admin-login-2fa): the server's stable error code (`errors[0].code` in the
   * canonical envelope, e.g. `EMAIL_UNVERIFIED`, `INVALID_CREDENTIALS`), so
   * callers branch on a code rather than on copy that may be reworded.
   * Before: only `status` + `message` were exposed. After: `code` (or null).
   */
  readonly code: string | null;
  constructor(message: string, status: number, requestId: string, body: unknown = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.requestId = requestId;
    this.body = body;
    this.code = errorCodeOf(body);
  }
  /** 401/403 etc. */
  get isAuth(): boolean {
    return this.status === 401;
  }
  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export type ApiMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export interface RequestOptions {
  method?: ApiMethod;
  body?: unknown;
  signal?: AbortSignal;
  /** Extra headers (e.g. step-up token). */
  headers?: Record<string, string>;
  /**
   * Opt-in transport retries for idempotent calls (network/5xx). Default 0.
   * FIX(admin-global-mutation-error): the client used to retry every GET twice
   * here AND React Query retried the failed query twice more — up to 9 requests
   * per failing read. Before: default 2 for GET. After: default 0; React Query's
   * `retry` (no retry on 4xx) is the single retry layer.
   */
  retries?: number;
  /** Skip JSON serialization (e.g. FormData uploads). */
  raw?: boolean;
  /**
   * FIX(admin-reauth-401): do NOT fire the global unauthorized listener when
   * this request 401s. For endpoints where 401 is an ordinary answer rather
   * than "your session is gone": a wrong step-up password (`/api/auth/reauth`),
   * bad credentials (`/api/auth/login`), a failed second factor
   * (`/api/auth/login/2fa`), and the `/api/auth/me` bootstrap (401 = logged out,
   * handled by the query). Before: every 401 logged the admin out and discarded
   * the open dialog. After: these callers handle their own 401.
   */
  suppressUnauthorized?: boolean;
}

// Listeners notified on a 401 so the app can redirect to login with return-to.
type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();
export function onUnauthorized(fn: UnauthorizedListener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

/** Fire the global unauthorized listeners by hand — for a caller that
 *  suppressed them (`suppressUnauthorized`) but then learned the 401 really
 *  was an expired session. */
export function notifyUnauthorized(): void {
  unauthorizedListeners.forEach((fn) => fn());
}

/**
 * FIX(admin-stepup-server-enforced): the server now enforces step-up on the
 * high-risk admin routes and refuses a missing/expired `x-step-up-token` with
 * `401 { code: 'STEP_UP_REQUIRED' }`. That 401 is NOT "your session is gone":
 * treating it like one logged the operator out mid-action. Before: every 401
 * fired the unauthorized listeners. After: a step-up refusal fires the
 * step-up listeners instead (the step-up store clears its elevation — see
 * features/auth/stepup.ts) and throws an ApiError whose message asks for the
 * password again, so every caller's inline error / toast reads correctly.
 */
export const STEP_UP_REQUIRED_CODE = 'STEP_UP_REQUIRED';
export const STEP_UP_RETRY_MESSAGE = 'Please confirm your password again.';

/** True for the server's step-up refusal (token missing, expired, or not yours). */
export function isStepUpRequiredError(err: unknown): err is ApiError {
  return err instanceof ApiError && err.status === 401 && err.code === STEP_UP_REQUIRED_CODE;
}

type StepUpRequiredListener = () => void;
const stepUpRequiredListeners = new Set<StepUpRequiredListener>();
/** Subscribe to server step-up refusals (used to drop a stale elevation). */
export function onStepUpRequired(fn: StepUpRequiredListener): () => void {
  stepUpRequiredListeners.add(fn);
  return () => stepUpRequiredListeners.delete(fn);
}

/** The canonical envelope's first error code (`errors[0].code`), else a bare
 *  `code`, else null. */
function errorCodeOf(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const b = body as { code?: unknown; errors?: { code?: unknown }[] };
  const first = Array.isArray(b.errors) ? b.errors[0]?.code : undefined;
  if (typeof first === 'string' && first) return first;
  if (typeof b.code === 'string' && b.code) return b.code;
  return null;
}

let requestSeq = 0;
function nextRequestId(): string {
  // Stable, monotonic, no Date.now/Math.random dependency for testability.
  requestSeq += 1;
  return `adm-${requestSeq.toString(36)}-${performance.now().toFixed(0)}`;
}

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${  name  }=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Repoint the admin app onto the dedicated, fully-audited admin API surface
 * (`/api/admin/*`). The server now exposes a separate admin endpoint for every
 * domain the panel touches; this single choke point rewrites the legacy product
 * paths the feature hooks still spell out (`/api/users`, `/api/floors`, …) to
 * their `/api/admin/*` equivalents — so the whole app moves to the admin surface
 * with no per-feature churn, and every admin call is auto-audited server-side.
 *
 * Only the domains that HAVE an admin route are remapped; paths that are already
 * `/api/admin/*`, the AI admin surface (`/api/ai/admin/*`), billing (no admin
 * route yet), auth, and the two product system endpoints the panel legitimately
 * reads (`incident-banner`, `health`) are passed through.
 *
 * Tracker + meetings/recordings are deliberately NOT a prefix rewrite here
 * (FIX(admin-org-scoped-reads)): their admin routes are READ-ONLY, so only the
 * GETs move to `/api/admin/tracker|meetings|recordings/*` — spelled out in the
 * feature hooks — while their writes stay on the org-scoped product routes.
 * This function sees a path, not a method, so it cannot make that split.
 */
export function toAdminPath(path: string): string {
  const [p, ...rest] = path.split('?');
  const qs = rest.length ? `?${rest.join('?')}` : '';
  const mapped = rewriteAdminPath(p);
  return mapped + qs;
}

// Product paths (segment-exact) that relocate to `/api/admin` + the same suffix.
const ADMIN_RELOCATED_BASES = [
  '/api/users',
  '/api/roles',
  '/api/permissions',
  '/api/rooms',
  '/api/floors',
  '/api/audit',
  '/api/security',
  '/api/incidents',
  '/api/files',
  '/api/workspaces',
  '/api/messaging',
  '/api/attendance',
];

function rewriteAdminPath(p: string): string {
  if (p.startsWith('/api/admin/')) return p; // params, log-*, impersonate, settings
  if (p.startsWith('/api/ai/')) return p; // AI admin lives under /api/ai/admin/*
  // System endpoints the panel legitimately reads on the product surface.
  if (p === '/api/system/incident-banner' || p === '/api/system/health') return p;
  // A few product paths whose admin home isn't a simple prefix swap.
  if (p === '/api/system/request-logs') return '/api/admin/security/request-logs';
  if (p === '/api/system/storage') return '/api/admin/storage';
  if (p.startsWith('/api/docs/admin')) return p.replace('/api/docs/admin', '/api/admin/docs');
  for (const base of ADMIN_RELOCATED_BASES) {
    if (p === base || p.startsWith(`${base}/`)) return `/api/admin${p.slice(4)}`;
  }
  return p;
}

/**
 * FIX(admin-api-base-urls): the request-URL builder is exported so the few
 * callers that can't go through `api()` (the XHR release upload, which needs
 * progress events) hit the SAME origin the rest of the app does.
 * Before: `url()` was module-private and the upload read an unset
 * `import.meta.env.VITE_API_BASE_URL`, POSTing to the admin's own origin whenever
 * the API is cross-origin. After: `apiUrl()` is the one builder for requests.
 */
export function apiUrl(path: string): string {
  if (path.startsWith('http')) return path;
  const mapped = toAdminPath(path);
  const base = env.apiBaseUrl.replace(/\/$/, '');
  return base ? `${base}${mapped}` : mapped;
}

/**
 * Resolve a server-returned URL (e.g. an upload's relative `/api/uploads/...`,
 * a recording's `/api/meetings/recordings/:id/media`) against the API origin so
 * it can be rendered as an `<img src>` / `<a href>` / media source.
 *
 * FIX(admin-api-base-urls): Before: relative server paths were rendered as-is,
 * which resolves against the ADMIN origin and 404s when the API is cross-origin
 * (e.g. docker's `ADMIN_API_BASE_URL=http://localhost:3001`). After: root-relative
 * paths get the API base prepended; absolute (`http:`, `https:`, `data:`, `blob:`,
 * protocol-relative) URLs and '' pass through unchanged, so it is idempotent.
 * No admin-path rewrite is applied — these are server-given resource links.
 */
export function absoluteApiUrl(path: string): string;
export function absoluteApiUrl(path: string | null | undefined): string | null;
export function absoluteApiUrl(path: string | null | undefined): string | null {
  if (path === null || path === undefined) return null;
  if (!path.startsWith('/') || path.startsWith('//')) return path;
  const base = env.apiBaseUrl.replace(/\/$/, '');
  return base ? `${base}${path}` : path;
}

const IDEMPOTENT = new Set<ApiMethod>(['GET']);

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  const requestId = nextRequestId();
  // FIX(admin-global-mutation-error): no transport retry unless asked for — see
  // `RequestOptions.retries`. Before: `IDEMPOTENT.has(method) ? 2 : 0`. After: 0.
  const maxRetries = opts.retries ?? 0;

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Request-Id': requestId,
    ...opts.headers,
  };

  let payload: BodyInit | undefined;
  if (opts.body !== undefined) {
    if (opts.raw) {
      payload = opts.body as BodyInit;
    } else {
      headers['Content-Type'] = 'application/json';
      payload = JSON.stringify(opts.body);
    }
  }

  // CSRF: send the double-submit token cookie on mutations if the backend sets it.
  if (!IDEMPOTENT.has(method)) {
    const csrf = readCookie('csrf');
    if (csrf) headers['X-CSRF-Token'] = csrf;
  }

  let attempt = 0;
  for (;;) {
    try {
      const res = await fetch(apiUrl(path), {
        method,
        headers,
        body: payload,
        credentials: 'include',
        signal: opts.signal,
      });

      if (!res.ok) {
        const body = await safeJson(res);
        // FIX(admin-stepup-server-enforced): a step-up refusal is a 401 that
        // must not log the admin out — see STEP_UP_REQUIRED_CODE.
        const stepUpRefused = res.status === 401 && errorCodeOf(body) === STEP_UP_REQUIRED_CODE;
        if (stepUpRefused) {
          stepUpRequiredListeners.forEach((fn) => fn());
          throw new ApiError(STEP_UP_RETRY_MESSAGE, res.status, requestId, body);
        }
        // FIX(admin-reauth-401): honour the per-request opt-out. Before: every 401
        // fired the listeners. After: skipped when `suppressUnauthorized` is set.
        if (res.status === 401 && !opts.suppressUnauthorized) {
          notifyUnauthorized();
        }
        let message = `Request failed (${res.status})`;
        // The backend emits the canonical error envelope
        // ({ status:'error', status_code, message, errors:[{code,field,message}] }).
        // Prefer envelope `message`, then a legacy bare `{ error }`, for gap endpoints.
        if (body && typeof body === 'object') {
          const envelope = body as {
            message?: unknown;
            error?: unknown;
            errors?: { message?: unknown }[];
          };
          const first = envelope.errors?.[0]?.message;
          if (typeof first === 'string' && first) message = first;
          else if (typeof envelope.message === 'string' && envelope.message) message = envelope.message;
          else if (typeof envelope.error === 'string' && envelope.error) message = envelope.error;
        }
        // Retry idempotent requests on transient 5xx.
        if (res.status >= 500 && attempt < maxRetries) {
          attempt += 1;
          await backoff(attempt);
          continue;
        }
        throw new ApiError(message, res.status, requestId, body);
      }

      if (res.status === 204) return undefined as T;
      return unwrapEnvelope<T>(await safeJson(res));
    } catch (err) {
      if (err instanceof ApiError) throw err;
      if (err instanceof DOMException && err.name === 'AbortError') throw err;
      // Network error — retry idempotent only.
      if (attempt < maxRetries) {
        attempt += 1;
        await backoff(attempt);
        continue;
      }
      // Don't surface low-level fetch errors ("Failed to fetch", etc.) — show a
      // plain connection message instead.
      throw new ApiError("Can't reach the server. Check your connection and try again.", 0, requestId);
    }
  }
}

/** True when a parsed body carries the canonical success/error envelope. */
function isEnvelope(body: unknown): body is { status: string; status_code: number; data?: unknown } {
  return (
    !!body &&
    typeof body === 'object' &&
    'status' in body &&
    typeof (body as { status_code?: unknown }).status_code === 'number'
  );
}

const PAGINATION_META_KEYS = new Set(['total', 'limit', 'page', 'pageSize', 'nextCursor', 'truncated']);

/**
 * Unwrap the canonical envelope into the shape admin hooks expect:
 *  - paginated list (envelope with array `data` + `meta`) → the admin's
 *    `{ items, total, page, pageSize, nextCursor }` list shape. Offset-paginated
 *    endpoints carry `meta.page`/`meta.pageSize` (honored here so the DataTable's
 *    page controls deep-paginate); cursor endpoints carry `meta.limit`/
 *    `meta.nextCursor` and fall back to page 1.
 *  - any other envelope → its `data`.
 *  - legacy bare body (not-yet-migrated real endpoints) →
 *    returned unchanged.
 */
function unwrapEnvelope<T>(body: unknown): T {
  if (!isEnvelope(body)) return body as T;
  const env = body as {
    data?: unknown;
    meta?: {
      total?: number;
      limit?: number;
      page?: number;
      pageSize?: number;
      nextCursor?: string | null;
      truncated?: boolean;
      [extra: string]: unknown;
    };
  };
  if (Array.isArray(env.data) && env.meta) {
    // Any meta key beyond the pagination ones rides along as `meta` (e.g. the push-devices
    // totals strip), only when present so every existing list keeps its exact shape.
    const extraMeta = Object.fromEntries(
      Object.entries(env.meta).filter(([k]) => !PAGINATION_META_KEYS.has(k)),
    );
    return {
      items: env.data,
      total: env.meta.total ?? env.data.length,
      page: env.meta.page ?? 1,
      pageSize: env.meta.pageSize ?? env.meta.limit ?? env.data.length,
      nextCursor: env.meta.nextCursor ?? null,
      ...(Object.keys(extraMeta).length > 0 ? { meta: extraMeta } : {}),
      // FIX(admin-org-scoped-reads): a list that stopped at a server-side cap
      // says so (`/api/admin/meetings` Upcoming); only present when true.
      ...(env.meta.truncated === true ? { truncated: true } : {}),
    } as T;
  }
  return env.data as T;
}

async function safeJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function backoff(attempt: number): Promise<void> {
  // Deterministic backoff (no jitter dependency on Math.random).
  const ms = Math.min(1000, 100 * 2 ** attempt);
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export const apiClient = {
  get: <T>(path: string, opts?: RequestOptions) => api<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    api<T>(path, { ...opts, method: 'POST', body }),
  put: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    api<T>(path, { ...opts, method: 'PUT', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    api<T>(path, { ...opts, method: 'PATCH', body }),
  delete: <T>(path: string, opts?: RequestOptions) => api<T>(path, { ...opts, method: 'DELETE' }),
};
